class HeartsNotesQuiz extends HTMLElement {
  constructor() {
    super();
    this.definition = {};
    this.meta = {};
    this.products = { primary: null, linked: [] };
    this.i18n = {};
    this.loadingSpinnerMarkup = '';
    this.questions = [];
    this.transitions = [];
    this.phase = 'welcome';
    this.currentIndex = 0;
    this.selected = null;
    this.answers = [];
    this.firstName = '';
    this.email = '';
    this.draftFirstName = '';
    this.draftEmail = '';
    this.error = '';
    this.isSubmitting = false;
    this.assessmentStep = -1;
    this.assessingTarget = 'score';
    this.assessmentRunId = 0;
    this.captureReady = false;
    this.noteRevealed = false;
    this.noteJustRevealed = false;
    this.timeouts = new Set();
    this.pendingWaits = new Map();
    this.submitController = null;
    this.resumeAvailable = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.audioContext = null;
    this.soundOn = true;
    this.handleClick = this.handleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.focusDialog = null;
  }

  connectedCallback() {
    if (this.movingForFocus) return;
    this.definition = this.readJson(this.dataset.definition) || {};
    this.meta = this.readJson(this.dataset.meta) || {};
    this.products = this.readJson(this.dataset.products) || { primary: null, linked: [] };
    this.i18n = this.readJson(this.dataset.i18n) || {};
    this.loadingSpinnerMarkup = this.readTemplate(this.dataset.loadingSpinner);

    if (typeof this.definition === 'string') {
      try {
        this.definition = JSON.parse(this.definition);
      } catch (error) {
        this.definition = {};
      }
    }

    this.questions = this.flattenQuestions(this.definition.blocks || []);
    this.transitions = Array.isArray(this.definition.transitions) ? this.definition.transitions : [];
    this.soundOn = this.getStoredSoundPreference();
    this.restoreProgress();
    this.addEventListener('click', this.handleClick);
    this.addEventListener('submit', this.handleSubmit);
    this.render();
    window.requestAnimationFrame(() => {
      this.classList.remove('hnq-loading');
      this.classList.add('hnq-ready');
    });
  }

  disconnectedCallback() {
    if (this.movingForFocus) return;
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('submit', this.handleSubmit);
    this.cancelPendingWork();
    this.audioContext?.close?.().catch(() => {});
    this.audioContext = null;
  }

  readJson(selector) {
    if (!selector) return null;
    const node = document.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent.trim());
    } catch (error) {
      return null;
    }
  }

  readTemplate(selector) {
    if (!selector) return '';
    const template = document.querySelector(selector);
    return template instanceof HTMLTemplateElement ? template.innerHTML.trim() : '';
  }

  renderLoadingSpinner() {
    return this.loadingSpinnerMarkup || this.readTemplate(this.dataset.loadingSpinner) || '<span class="hnv2-animated-logo hnv2-animated-logo--fallback" aria-hidden="true"></span>';
  }

  copy(key, fallback) {
    const value = this.i18n?.[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  flattenQuestions(blocks) {
    const result = [];

    blocks.forEach((block) => {
      (block.questions || []).forEach((question) => {
        result.push({
          ...question,
          blockId: block.id,
          blockLabel: block.label || ''
        });
      });
    });

    return result;
  }

  // Result mode changes presentation, never the authored scoring/band selection.
  get resultMode() { return this.definition.resultMode === 'analysis' ? 'analysis' : 'score'; }
  get isAnalysis() { return this.resultMode === 'analysis'; }

  get total() {
    return this.questions.length;
  }

  get score() {
    return this.questions.reduce((sum, question, index) => {
      return sum + (this.answers[index] === question.correct ? 1 : 0);
    }, 0);
  }

  get scoreBand() {
    const score = this.score;
    const bands = Array.isArray(this.definition.scoreBands) ? this.definition.scoreBands : [];
    return bands.find((band) => score >= Number(band.min) && score <= Number(band.max)) || {
      title: 'Your result',
      message: 'Thank you for taking this Hearts Notes quiz.'
    };
  }

  get currentQuestion() {
    return this.questions[this.currentIndex];
  }

  get matchedLetter() {
    const score = this.score;
    const letters = Array.isArray(this.definition.letters) ? this.definition.letters : [];
    return letters.find((letter) => score >= Number(letter.min) && score <= Number(letter.max));
  }

  get matchedAnalysis() {
    const score = this.score;
    const items = Array.isArray(this.definition.analysis) ? this.definition.analysis : [];
    return items.find((item) => score >= Number(item.min) && score <= Number(item.max));
  }

  get matchedRecommendation() {
    const score = this.score;
    const items = Array.isArray(this.definition.recommendationBands) ? this.definition.recommendationBands : [];
    return items.find((item) => score >= Number(item.min) && score <= Number(item.max));
  }

  get recommendationSettings() {
    return this.definition.recommendation || this.definition.promo || {};
  }

  get displayName() {
    return this.firstName ? this.firstName : '';
  }

  greet(text) {
    return this.displayName ? text.replace('{firstName}', this.displayName) : text.replace('{firstName}, ', '').replace('{firstName}', '');
  }

  templateText(text, fallback = '', firstName = this.displayName || '') {
    const value = String(text || fallback || '');
    const modeText = this.isAnalysis ? value.replace(/If your score was low/gi, 'If only a few patterns stood out') : value;
    return modeText
      .replace(/\{firstName\}/g, firstName)
      .replace(/\{name\}/g, firstName)
      .replace(/\s+,/g, ',')
      .replace(/^,\s*/, '')
      .trim();
  }

  get assessmentLines() {
    const custom = this.definition.assessmentLines;
    if (Array.isArray(custom) && custom.length) return custom;

    return [
      'reading your answers',
      'Checking your quiz pattern',
      this.isAnalysis ? 'Preparing your personal analysis' : 'Preparing your score',
      'Building your answer report',
      'Finding the Hearts Notes material connected to this result'
    ];
  }

  get reportAssessmentLines() {
    return [
      'Checking every answer',
      'Preparing your explanations',
      'Opening your private note'
    ];
  }

  get activeAssessmentLines() {
    return this.assessingTarget === 'reveal' ? this.reportAssessmentLines : this.assessmentLines;
  }

  escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  icon(name) {
    return this.meta?.icons?.[name] || '';
  }

  renderPersonalized(value) {
    const text = String(value || '');
    const name = this.displayName;
    if (!name || !text.includes(name)) return this.escape(text);

    return text
      .split(name)
      .map((part, index, parts) => `${this.escape(part)}${index < parts.length - 1 ? `<strong class="hnq-person-name">${this.escape(name)}</strong>` : ''}`)
      .join('');
  }

  getStoredSoundPreference() {
    try {
      const stored = window.localStorage.getItem('hn_quiz_sound_on');
      return stored === null ? true : stored === 'true';
    } catch (error) {
      return true;
    }
  }

  setStoredSoundPreference(value) {
    try {
      window.localStorage.setItem('hn_quiz_sound_on', String(value));
    } catch (error) {}
  }

  ensureAudioContext() {
    if (!this.soundOn || (!window.AudioContext && !window.webkitAudioContext)) return null;

    if (!this.audioContext) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContextClass();
    }

    if (this.audioContext.state === 'suspended') this.audioContext.resume().catch(() => {});
    return this.audioContext;
  }

  playTone(frequency, duration = 0.07, delay = 0, gainValue = 0.028) {
    const context = this.ensureAudioContext();
    if (!context) return;

    const start = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(gainValue, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.03);
  }

  playFeedback(name) {
    if (!this.soundOn) return;

    if (name === 'select') {
      this.playTone(660);
    } else if (name === 'milestone') {
      this.playTone(523.25, 0.13);
      this.playTone(659.25, 0.15, 0.05, 0.024);
    } else if (name === 'reveal') {
      this.playTone(659.25, 0.14);
      this.playTone(783.99, 0.18, 0.06, 0.024);
    } else {
      this.playTone(560, 0.055, 0, 0.02);
    }
  }

  vibrate(pattern = 8) {
    if (!this.reducedMotion && 'vibrate' in navigator) navigator.vibrate(pattern);
  }

  toggleSound() {
    this.soundOn = !this.soundOn;
    this.setStoredSoundPreference(this.soundOn);
    if (this.soundOn) this.playFeedback('select');
    const button = this.querySelector('[data-action="sound"]');
    if (button) {
      button.setAttribute('aria-pressed', String(this.soundOn));
      button.setAttribute(
        'aria-label',
        this.soundOn ? this.copy('turnSoundOff', 'Turn sound off') : this.copy('turnSoundOn', 'Turn sound on')
      );
      button.classList.toggle('is-on', this.soundOn);
      button.querySelector('[data-hnq-sound-label]').textContent = this.soundOn
        ? this.copy('soundOn', 'Sound on')
        : this.copy('soundOff', 'Sound off');
      button.focus({ preventScroll: true });
    }
  }

  get progressStorageKey() {
    const slug = this.definition.slug || this.meta.slug || window.location.pathname;
    return `hn_quiz_progress:${slug}`;
  }

  restoreProgress() {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(this.progressStorageKey) || 'null');
      if (!saved || !Array.isArray(saved.answers)) return;

      this.answers = saved.answers.slice(0, this.total);
      const firstUnanswered = this.questions.findIndex((_, index) => this.answers[index] === null || this.answers[index] === undefined);
      this.currentIndex = firstUnanswered === -1
        ? Math.max(0, this.total - 1)
        : Math.max(0, firstUnanswered);
      this.selected = this.answers[this.currentIndex] ?? null;
      this.resumeAvailable = this.answers.some((answer) => answer !== null && answer !== undefined);
    } catch (error) {
      this.resumeAvailable = false;
    }
  }

  saveProgress() {
    try {
      window.sessionStorage.setItem(this.progressStorageKey, JSON.stringify({
        answers: this.answers,
        currentIndex: this.currentIndex,
        updatedAt: Date.now()
      }));
    } catch (error) {}
  }

  clearProgress() {
    try {
      window.sessionStorage.removeItem(this.progressStorageKey);
    } catch (error) {}
    this.resumeAvailable = false;
  }

  wait(duration) {
    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => {
        this.timeouts.delete(timeout);
        this.pendingWaits.delete(timeout);
        resolve();
      }, duration);
      this.timeouts.add(timeout);
      this.pendingWaits.set(timeout, resolve);
    });
  }

  schedule(callback, duration) {
    const timeout = window.setTimeout(() => {
      this.timeouts.delete(timeout);
      callback();
    }, duration);
    this.timeouts.add(timeout);
    return timeout;
  }

  cancelPendingWork() {
    this.timeouts.forEach((timeout) => {
      window.clearTimeout(timeout);
      this.pendingWaits.get(timeout)?.();
    });
    this.timeouts.clear();
    this.pendingWaits.clear();
    this.submitController?.abort();
    this.submitController = null;
  }

  scrollToQuizCard() {
    if (this.focusDialog) { this.focusDialog.scrollTop = 0; return; }
    const shell = this.querySelector('.hnq-shell') || this.querySelector('.hnq-card') || this;
    if (!shell || !shell.getBoundingClientRect) return;

    window.requestAnimationFrame(() => {
      const rect = shell.getBoundingClientRect();
      const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const header = document.querySelector('site-sticky-header');
      const headerHeight = header?.getBoundingClientRect().height || 0;
      const offset = headerHeight + 24;
      const targetY = Math.max(0, currentY + rect.top - offset);

      window.scrollTo({
        top: targetY,
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
    });
  }

  renderAndScroll(focusSelector = '') {
    this.render();
    this.scrollToQuizCard();
    if (focusSelector) {
      window.requestAnimationFrame(() => {
        this.querySelector(focusSelector)?.focus({ preventScroll: true });
      });
    }
  }

  setPhase(phase) {
    this.phase = phase;
    this.error = '';
    this.playFeedback('tap');
    this.renderAndScroll('[data-hnq-screen-title]');
  }

  startQuiz() {
    this.phase = 'question';
    if (!this.resumeAvailable) {
      this.currentIndex = 0;
      this.selected = this.answers[0] ?? null;
    }
    this.playFeedback('tap');
    this.vibrate();
    this.renderAndScroll('[data-hnq-screen-title]');
  }

  restartQuiz() {
    this.cancelPendingWork();
    this.clearProgress();
    this.answers = [];
    this.currentIndex = 0;
    this.selected = null;
    this.phase = 'question';
    this.error = '';
    this.playFeedback('tap');
    this.vibrate();
    this.renderAndScroll('[data-hnq-screen-title]');
  }

  selectOption(index, sourceButton) {
    this.selected = index;
    this.querySelectorAll('.hnq-option').forEach((option) => {
      const selected = Number(option.dataset.index) === index;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-pressed', String(selected));
    });
    const nextButton = this.querySelector('[data-action="next"]');
    if (nextButton) nextButton.disabled = false;
    this.playFeedback('select');
    this.vibrate(10);
    sourceButton?.focus({ preventScroll: true });
  }

  previousQuestion() {
    if (this.phase === 'question' && this.currentIndex === 0) {
      this.phase = 'welcome';
      this.renderAndScroll();
      return;
    }

    if (this.phase === 'question') {
      this.currentIndex -= 1;
      this.selected = this.answers[this.currentIndex] ?? null;
      this.renderAndScroll('[data-hnq-screen-title]');
      return;
    }

    if (this.phase === 'capture') {
      this.assessmentRunId += 1;
      this.phase = 'question';
      this.currentIndex = Math.max(0, this.total - 1);
      this.selected = this.answers[this.currentIndex] ?? null;
      this.renderAndScroll('[data-hnq-screen-title]');
    }
  }

  nextQuestion() {
    if (this.selected === null || this.selected === undefined) return;

    this.answers[this.currentIndex] = this.selected;
    this.saveProgress();
    this.playFeedback('tap');
    this.vibrate();

    const completedQuestion = this.currentIndex + 1;
    const transition = this.transitions.find((item) => Number(item.afterQuestion) === completedQuestion);

    if (transition) {
      this.phase = 'transition';
      this.playFeedback('milestone');
      this.vibrate([8, 36, 8]);
      this.renderAndScroll('[data-hnq-screen-title]');
      return;
    }

    this.advanceAfterQuestion();
  }

  advanceAfterQuestion() {
    if (this.currentIndex + 1 >= this.total) {
      this.selected = null;
      this.startAssessment('capture');
      return;
    }

    this.currentIndex += 1;
    this.selected = this.answers[this.currentIndex] ?? null;
    this.phase = 'question';
    this.renderAndScroll('[data-hnq-screen-title]');
  }

  continueAfterTransition() {
    this.playFeedback('tap');
    this.advanceAfterQuestion();
  }

  async startAssessment(target = 'score') {
    const runId = this.assessmentRunId + 1;
    this.assessmentRunId = runId;
    this.assessingTarget = target;
    this.error = '';
    this.assessmentStep = -1;
    this.isSubmitting = false;
    this.captureReady = false;
    this.phase = target === 'capture' ? 'capture' : 'assessing';
    this.playFeedback('milestone');
    this.renderAndScroll('[data-hnq-screen-title]');

    await this.runAssessmentAnimation(runId);
    if (!this.isConnected || this.assessmentRunId !== runId) return;

    if (target === 'capture') {
      this.captureReady = true;
      this.assessmentStep = this.activeAssessmentLines.length;
      this.playFeedback('reveal');
    } else {
      this.phase = target;
      this.assessmentStep = -1;
      this.playFeedback('reveal');
    }
    this.renderAndScroll('[data-hnq-screen-title]');
  }

  submitLead(event) {
    event.preventDefault();
    this.submitLeadFromForm(event.target);
  }

  async submitLeadFromForm(form) {
    if (!form || this.isSubmitting) return;

    const firstName = String(new FormData(form).get('firstName') || '').trim();
    const email = String(new FormData(form).get('email') || '').trim();
    const emailInput = form.elements.email;

    this.draftFirstName = firstName;
    this.draftEmail = email;

    if (!firstName) {
      this.error = 'Please enter your first name.';
      this.render();
      this.querySelector('[name="firstName"]')?.focus();
      return;
    }

    if (!emailInput?.validity.valid) {
      this.error = 'Please enter a complete email address, for example name@example.com.';
      this.render();
      this.querySelector('[name="email"]')?.focus();
      return;
    }

    this.error = '';
    this.isSubmitting = true;
    this.playFeedback('tap');
    this.render();

    try {
      await this.postWebhook(this.buildPayload({ firstName, email }));
    } catch (_error) {
      if (!this.isConnected || this.phase !== 'capture') return;
      this.isSubmitting = false;
      this.error = _error.formMessage || 'We could not confirm your request. Please try again, or continue without email to see your results.';
      this.render();
      return;
    }
    if (!this.isConnected || this.phase !== 'capture') return;

    this.firstName = firstName;
    this.email = email;
    this.isSubmitting = false;
    this.phase = 'score';
    this.clearProgress();
    this.playFeedback('reveal');
    this.vibrate([10, 30, 10]);
    this.renderAndScroll('[data-hnq-screen-title]');
  }

  continueWithoutEmail() {
    this.submitController?.abort();
    this.firstName = '';
    this.email = '';
    this.draftFirstName = '';
    this.draftEmail = '';
    this.error = '';
    this.isSubmitting = false;
    this.phase = 'score';
    this.clearProgress();
    this.playFeedback('reveal');
    this.vibrate([10, 30, 10]);
    this.renderAndScroll('[data-hnq-screen-title]');
  }

  async runAssessmentAnimation(runId = this.assessmentRunId) {
    const lines = this.activeAssessmentLines;
    const totalDuration = 6000;
    const stepDelay = lines.length ? totalDuration / lines.length : totalDuration;

    for (let index = 0; index < lines.length; index += 1) {
      if (!this.isConnected || this.assessmentRunId !== runId) return;
      this.assessmentStep = index;
      this.querySelectorAll('[data-hnq-assessment-line]').forEach((line, lineIndex) => {
        line.classList.toggle('is-visible', lineIndex <= index);
        line.classList.toggle('is-current', lineIndex === index);
        line.classList.toggle('is-complete', lineIndex < index);
        line.setAttribute('aria-hidden', String(lineIndex > index));
      });
      if (index > 0) this.playFeedback('select');
      await this.wait(stepDelay);
    }
  }

  sanitizeTag(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_{2,}/g, '_');
  }

  absoluteUrl(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).toString();
    } catch (error) {
      return String(url || '');
    }
  }

  attributedUrl(value, medium, content) {
    if (!value) return '';
    const url = new URL(value, window.location.origin);
    url.searchParams.set('utm_source', 'heartsnotes');
    url.searchParams.set('utm_medium', medium);
    url.searchParams.set('utm_campaign', this.definition.slug || this.meta.slug || 'quiz');
    url.searchParams.set('utm_content', content);
    return url.toString();
  }

  buildPreviewUrl(product, campaign) {
    if (!product?.id) return '';

    const url = new URL('/pages/free-preview', window.location.origin);
    url.searchParams.set('pid', product.id);
    url.searchParams.set('utm_source', 'heartsnotes');
    url.searchParams.set('utm_medium', 'quiz');
    url.searchParams.set('utm_campaign', campaign || 'quiz');
    return url.toString();
  }

  productPayload(product, campaign) {
    if (!product) return null;

    return {
      id: product.id || null,
      handle: product.handle || '',
      title: product.title || '',
      short_name: product.customShortNameText || product.shortTitleText || product.shortTitle || product.title || '',
      url: this.absoluteUrl(product.url || ''),
      image: product.image ? this.absoluteUrl(product.image) : '',
      preview_url: product.hasPreview === true ? this.buildPreviewUrl(product, campaign) : ''
    };
  }

  buildPayload({ firstName = this.firstName, email = this.email } = {}) {
    const band = this.scoreBand;
    const analysis = this.matchedAnalysis || {};
    const personalNote = this.matchedLetter || {};
    const recommendationCopy = this.getRecommendationCopy(firstName);
    const quizSlug = this.definition.slug || this.meta.slug || '';
    const quizTitle = this.definition.meta?.title || this.meta.title || document.title;
    const categories = Array.isArray(this.meta.categories) ? this.meta.categories : [];
    const fallbackCategory = categories.find((category) => category && (category.slug || category.name)) || {};
    const primaryCategorySlug = this.meta.primaryCategorySlug || this.meta.category || fallbackCategory.slug || '';
    const primaryCategoryLabel = this.meta.primaryCategoryLabel || this.meta.categoryLabel || fallbackCategory.name || '';
    const campaign = primaryCategorySlug || 'quiz';
    const submittedAt = new Date().toISOString();
    const pageUrl = this.absoluteUrl(this.meta.pageUrl || window.location.href);
    const primaryProduct = this.products?.primary || null;
    const linkedProducts = Array.isArray(this.products?.linked) ? this.products.linked : [];

    return {
      source: 'shopify_quiz',
      store: window.location.hostname,
      lead_intent: 'quiz',
      first_name: firstName,
      email,
      quiz: {
        slug: quizSlug,
        title: quizTitle,
        taxonomy: this.meta.taxonomy || {},
        primary_category: {
          name: primaryCategoryLabel,
          slug: primaryCategorySlug
        },
        categories: categories.map((category) => ({
          name: category?.name || '',
          slug: category?.slug || ''
        }))
      },
      result: {
        mode: this.resultMode,
        score: this.isAnalysis ? null : this.score,
        total: this.isAnalysis ? null : this.total,
        score_percentage: this.isAnalysis ? null : (this.total > 0 ? Math.round((this.score / this.total) * 100) : 0),
        score_band: {
          title: this.templateText(band.title, '', firstName),
          message: this.templateText(band.message, '', firstName)
        },
        analysis: {
          headline: this.templateText(analysis.headline, '', firstName),
          body: this.templateText(analysis.body, '', firstName)
        },
        note_for_you: {
          title: 'A note for you',
          body: this.templateText(personalNote.body || '', '', firstName)
        },
        recommendation: {
          headline: recommendationCopy.headline || '',
          body: recommendationCopy.body || '',
          primary_product: this.productPayload(primaryProduct, campaign),
          linked_products: linkedProducts.map((product) => this.productPayload(product, campaign)).filter(Boolean)
        }
      },
      submitted_at: submittedAt,
      page_url: pageUrl,
      referrer: document.referrer || '',
      utm_source: 'heartsnotes',
      utm_medium: 'quiz',
      utm_campaign: campaign
    };
  }

  async postWebhook(payload) {
    const url = this.dataset.webhookUrl;
    if (!url) throw new Error('Capture is unavailable');

    this.submitController?.abort();
    this.submitController = new AbortController();
    const controller = this.submitController;
    const timeout = setTimeout(() => controller.abort(), 165000);

    try {
      if (!window.HNFormProtection) throw new Error('Verification unavailable');
      const protectedPayload = await window.HNFormProtection.prepareLead(payload, this.querySelector('form'));
      if (controller.signal.aborted) throw new Error('Request cancelled');
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        signal: this.submitController.signal,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(protectedPayload)
      });
      const result = await response.json();
      if (!response.ok || result.success !== true || result.stored !== true) {
        const failure = new Error('Capture was not acknowledged');
        failure.formMessage = window.HNFormProtection.message(result.code);
        throw failure;
      }
    } finally {
      clearTimeout(timeout);
      this.submitController = null;
    }
  }

  productList() {
    const primary = this.products?.primary ? [this.products.primary] : [];
    const linked = Array.isArray(this.products?.linked) ? this.products.linked : [];
    const seen = new Set();

    return [...primary, ...linked].filter((product) => {
      const key = product?.id || product?.handle || product?.url;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  handleClick(event) {
    const button = event.target.closest('[data-action]');
    if (!button || !this.contains(button)) return;

    const action = button.dataset.action;

    if (action === 'start') this.startQuiz();
    if (action === 'restart') this.restartQuiz();
    if (action === 'select') this.selectOption(Number(button.dataset.index), button);
    if (action === 'next') this.nextQuestion();
    if (action === 'previous') this.previousQuestion();
    if (action === 'continue') this.continueAfterTransition();
    if (action === 'capture') this.submitLeadFromForm(button.closest('form') || this.querySelector('[data-hn-capture-form]'));
    if (action === 'skip-capture') this.continueWithoutEmail(button);
    if (action === 'analysis') this.startAssessment('reveal');
    if (action === 'reveal') this.setPhase('reveal');
    if (action === 'score') this.setPhase('score');
    if (action === 'focus-mode') this.toggleFocusMode();
    if (action === 'sound') this.toggleSound();
    if (action === 'toggle-note') {
      this.noteRevealed = true;
      this.noteJustRevealed = true;
      this.playFeedback('reveal');
      this.vibrate([8, 28, 8]);
      this.render();
      this.schedule(() => { this.noteJustRevealed = false; }, 900);
    }
    if (action === 'share-quiz') this.shareQuiz(button);
  }

  handleSubmit(event) {
    if (event.target.matches('[data-hn-capture-form]')) {
      this.submitLead(event);
    }
  }

  render() {
    this.dataset.phase = this.phase;
    this.closest('.hn-quiz-page')?.setAttribute('data-quiz-phase', this.phase);
    const share = this.phase === 'score' || this.phase === 'reveal' ? this.renderShareQuiz() : '';

    if (!this.total) {
      this.innerHTML = this.renderShell(this.renderMissing());
      return;
    }

    const screens = {
      welcome: () => this.renderWelcome(),
      question: () => this.renderQuestion(),
      transition: () => this.renderTransition(),
      capture: () => this.renderCapture(),
      assessing: () => this.renderAssessing(),
      score: () => this.renderScore(),
      reveal: () => this.renderReveal()
    };

    this.innerHTML = `${this.renderShell(screens[this.phase]?.() || screens.welcome())}${share}`;
  }

  toggleFocusMode() {
    if (this.focusDialog) { this.focusDialog.close(); return; }
    const dialog = document.createElement('dialog');
    dialog.className = 'hnq-focus-dialog';
    dialog.setAttribute('aria-label', this.meta.title || 'Hearts Notes quiz');
    const anchor = document.createComment('quiz-focus-origin');
    this.before(anchor);
    this.focusDialog = dialog;
    this.focusOrigin = anchor;
    this.movingForFocus = true;
    document.body.append(dialog);
    dialog.append(this);
    this.movingForFocus = false;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.addEventListener('close', () => {
      this.movingForFocus = true;
      anchor.replaceWith(this);
      this.movingForFocus = false;
      this.focusDialog = null;
      document.body.style.overflow = oldOverflow;
      dialog.remove();
      const button = this.querySelector('[data-action="focus-mode"]');
      button?.setAttribute('aria-pressed', 'false');
      if (button) button.textContent = 'Full screen';
      button?.focus();
    }, { once: true });
    dialog.showModal();
    const button = this.querySelector('[data-action="focus-mode"]');
    button?.setAttribute('aria-pressed', 'true');
    if (button) button.textContent = 'Exit full screen';
    button?.focus();
  }

  renderShell(content) {
    return `
      <div class="hnq-focus-toolbar"><span>${this.isAnalysis ? 'Personal analysis' : 'Score + explanations'}</span><button type="button" class="hnq-text-button" data-action="focus-mode" aria-pressed="${Boolean(this.focusDialog)}">${this.focusDialog ? 'Exit full screen' : 'Full screen'}</button></div>
      <div class="hnq-shell">
        ${content}
      </div>
      <div class="hnq-shell-bottom">
        ${this.renderBackToQuizzes('hnq-back-link--shell-bottom')}
        <button
          class="hnq-sound-control ${this.soundOn ? 'is-on' : ''}"
          type="button"
          data-action="sound"
          aria-pressed="${this.soundOn}"
          aria-label="${this.escape(this.soundOn ? this.copy('turnSoundOff', 'Turn sound off') : this.copy('turnSoundOn', 'Turn sound on'))}"
        >
          <span class="hnq-sound-control__indicator" aria-hidden="true"></span>
          <span data-hnq-sound-label>${this.escape(this.soundOn ? this.copy('soundOn', 'Sound on') : this.copy('soundOff', 'Sound off'))}</span>
        </button>
      </div>
    `;
  }

  shareUrl() {
    const canonical = this.meta.pageUrl || window.location.href;
    try {
      const url = new URL(canonical, window.location.origin);
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch (error) {
      return window.location.href.split('#')[0].split('?')[0];
    }
  }

  shareText() {
    const title = this.definition.meta?.title || this.meta.title || 'Hearts Notes Quiz';
    return `${title} | Hearts Notes`;
  }

  renderShareQuiz() {
    const url = this.shareUrl();

    return `
      <div class="hnq-share-row">
        <button class="hnq-share-action" type="button" data-action="share-quiz" data-share-url="${this.escape(url)}">
          <span class="hnq-share-action__icon" aria-hidden="true">${this.icon('share')}</span>
          <span data-share-label>${this.escape(this.copy('share', 'Share this quiz'))}</span>
        </button>
      </div>
    `;
  }

  async shareQuiz(button) {
    const url = button?.dataset?.shareUrl || this.shareUrl();
    const label = button?.querySelector('[data-share-label]');
    const original = label ? label.textContent : '';
    const done = () => {
      if (label) {
        label.textContent = this.copy('linkCopied', 'Link copied');
        this.schedule(() => { label.textContent = original || this.copy('share', 'Share this quiz'); }, 1700);
      }
      this.playFeedback('select');
      this.vibrate();
    };

    if (navigator.share) {
      try {
        await navigator.share({
          title: this.shareText(),
          text: this.shareText(),
          url
        });
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => {
        window.prompt(this.copy('copyLinkPrompt', 'Copy this quiz link:'), url);
      });
      return;
    }

    window.prompt(this.copy('copyLinkPrompt', 'Copy this quiz link:'), url);
  }

  renderMissing() {
    return `
      <div class="hnq hnq-card hnq-empty">
        <p class="hnq-eyebrow">Quiz setup required</p>
        <h1 class="hnq-title">Questions are missing</h1>
        <p class="hnv2-quiz-message">Add valid quiz questions inside the Quiz Definition JSON.</p>
      </div>
    `;
  }

  renderBackToQuizzes(className = '') {
    const href = this.meta.quizIndexUrl || '/pages/quiz';
    return `<a class="hnq-back-link ${className}" href="${this.escape(href)}">← ${this.escape(this.copy('backToQuizzes', 'Back to all quizzes'))}</a>`;
  }

  renderChallengeRibbon() {
    if (this.isAnalysis) return '';
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');
    const score = params.get('score');
    if (!from || !score) return '';

    return `<p class="hnq-challenge">${this.escape(from)} scored ${this.escape(score)}/${this.total}. Can you beat them?</p>`;
  }


  formatPlayedCountLabel(value) {
    const raw = value === null || value === undefined ? '' : String(value).trim();
    if (!raw) return '';
    if (/^[\d\s,.]+$/.test(raw)) {
      return `${raw} played`;
    }
    return raw;
  }

  renderWelcomeCategories() {
    const categories = Array.isArray(this.meta.categories) ? this.meta.categories : [];
    const primarySlug = this.meta.primaryCategorySlug || this.meta.category || '';
    const formatTag = (value) => {
      const label = String(value || 'Quiz').trim();
      return /^quiz\b/i.test(label) ? label : `Quiz · ${label}`;
    };
    const secondary = categories
      .filter((category) => category && category.slug && category.slug !== primarySlug)
      .map((category) => `<span class="hn-quiz-card__category hn-quiz-card__category--secondary">${this.escape(formatTag(category.name || category.slug))}</span>`)
      .join('');

    return `
      <div class="hn-quiz-card__categories hnv2-quiz-hero__categories">
        <span class="hn-quiz-card__category hn-quiz-card__category--primary">${this.escape(formatTag(this.meta.primaryCategoryLabel || this.meta.categoryLabel))}</span>
        ${secondary}
      </div>
    `;
  }

  renderWelcomeMetadata() {
    const played = this.formatPlayedCountLabel(this.meta.playedCountLabel);
    return `
      <div class="hn-quiz-card__metadata hnv2-quiz-hero__metadata" aria-label="${this.escape(this.copy('details', 'Quiz details'))}">
        <span class="hn-quiz-card__meta hnv2-quiz-hero__questions">
          ${this.total} ${this.escape(this.total === 1 ? this.copy('question', 'question') : this.copy('questions', 'questions'))}
        </span>
        <span class="hn-quiz-card__meta">
          <span class="svg-wrapper" aria-hidden="true">${this.icon('stopwatch')}</span>
          ${this.escape(this.meta.timeLabel || '3 min')}
        </span>
        ${played ? `
          <span class="hn-quiz-card__meta hn-quiz-card__meta--played hnv2-quiz-hero__played">
            <span class="svg-wrapper" aria-hidden="true">${this.icon('plays')}</span>
            ${this.escape(played)}
          </span>
        ` : ''}
      </div>
    `;
  }

  renderWelcome() {
    const meta = this.definition.meta || {};
    const image = this.meta.image;
    const suppliedEyebrow = String(meta.preHeadline || this.meta.primaryCategoryLabel || this.meta.categoryLabel || '').trim();
    const eyebrow = /\bquiz\b/i.test(suppliedEyebrow)
      ? suppliedEyebrow
      : `${suppliedEyebrow || 'Faith-filled'} quiz`;

    return `
      <div class="hnq hnq-card hnv2-quiz-hero">
        <div class="hnv2-quiz-hero__layout">
          <div class="hnv2-quiz-hero__media-stack">
            <div class="hnv2-quiz-hero__media">
              ${image ? `<img src="${this.escape(image)}" alt="${this.escape(this.meta.imageAlt || this.meta.title || meta.title || 'Quiz image')}">` : '<div class="hnq-media__placeholder"></div>'}
              ${this.meta.badge ? `<span class="badge badge--marketing hn-quiz-card__badge">${this.escape(this.meta.badge)}</span>` : ''}
              <div class="hnv2-quiz-hero__overlay">
                <p class="hnv2-eyebrow hnv2-quiz-hero__eyebrow">${this.escape(eyebrow)}</p>
                <h1 class="hnv2-editorial-title hnv2-quiz-hero__title" data-hnq-screen-title tabindex="-1">${this.escape(meta.title || this.meta.title || 'Hearts Notes Quiz')}</h1>
                ${this.renderWelcomeMetadata()}
              </div>
            </div>
          </div>
          <div class="hnv2-quiz-hero__body">
            ${this.renderWelcomeCategories()}
            ${this.renderChallengeRibbon()}
            ${this.meta.seoIntro ? `<p class="hnv2-quiz-hero__description">${this.escape(this.meta.seoIntro)}</p>` : ''}
            <p class="hnv2-quiz-hero__hook">${this.escape(meta.subtitle || this.meta.description || '')}</p>
            <div class="hnq-actions hnq-actions--start ${this.resumeAvailable ? 'hnq-actions--resume' : ''}">
              ${this.resumeAvailable ? `
                <button class="button button--secondary hnq-button hnq-button--outline" type="button" data-action="start">${this.escape(this.copy('resume', 'Continue where I left off'))}</button>
                <button class="button hnq-button" type="button" data-action="restart">${this.escape(this.copy('restart', 'Restart quiz'))}</button>
              ` : `
                <button class="button hnq-button" type="button" data-action="start">${this.escape(meta.ctaStart || 'Begin the quiz')}</button>
              `}
            </div>
            ${this.resumeAvailable ? '<p class="hnq-under-note">Your answers are saved on this device for this visit.</p>' : ''}
            ${meta.ctaUnderStart ? `<p class="hnq-under-note">${this.escape(meta.ctaUnderStart)}</p>` : ''}
            ${meta.welcomeStat ? `
              <aside class="hnq-insight hnq-insight--welcome">
                <span class="hnq-insight__icon" aria-hidden="true">${this.icon('info')}</span>
                <div><strong>Did you know?</strong><p>${this.escape(meta.welcomeStat)}</p></div>
              </aside>
            ` : ''}
            ${this.readTemplate(this.dataset.classification)}
          </div>
        </div>
      </div>
    `;
  }

  renderQuestionProgress(position = this.currentIndex + 1, showLabel = true) {
    const current = Math.min(this.total, Math.max(1, Number(position) || 1));
    const label = `${this.copy('question', 'Question')} ${current} ${this.copy('of', 'of')} ${this.total}`;

    return `
      <div class="hnq-question-progress">
        ${showLabel ? `<p class="hnq-question-progress__label">${this.escape(label)}</p>` : ''}
        <div
          class="hnq-question-progress__segments"
          role="progressbar"
          aria-label="${this.escape(label)}"
          aria-valuemin="1"
          aria-valuemax="${this.total}"
          aria-valuenow="${current}"
          style="--hnq-progress-total: ${this.total};"
        >
          ${Array.from({ length: this.total }, (_, index) => `
            <span class="hnq-question-progress__segment ${index < current - 1 ? 'is-complete' : ''} ${index === current - 1 ? 'is-current' : ''}" aria-hidden="true"></span>
          `).join('')}
        </div>
      </div>
    `;
  }

  renderQuestion() {
    const question = this.currentQuestion;
    const count = this.currentIndex + 1;
    const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
    const didYouKnow = Array.isArray(this.definition.didYouKnow) && count % 3 === 0
      ? this.definition.didYouKnow[(count - 1) % this.definition.didYouKnow.length]
      : '';

    return `
      <div class="hnq hnq-card hnq-question-wrap">
        <div class="hnq-question-card">
          ${this.renderQuestionProgress(count)}
          <div class="hnq-question-prompt">
            <h2 class="hnq-question-title" data-hnq-screen-title tabindex="-1">${this.escape(question.q)}</h2>
            ${question.blockLabel ? `
              <p class="hnq-question-topic"><span>${this.escape(this.copy('topic', 'Topic'))}:</span> ${this.escape(question.blockLabel)}</p>
            ` : ''}
          </div>
          <div class="hnq-options">
            ${(question.options || []).map((option, index) => `
              <button class="hnq-option ${this.selected === index ? 'is-selected' : ''}" type="button" data-action="select" data-index="${index}" aria-pressed="${this.selected === index ? 'true' : 'false'}">
                <span class="hnq-option__letter">${letters[index] || index + 1}</span>
                <span class="hnq-option__text">${this.escape(option)}</span>
                <span class="hnq-option__check" aria-hidden="true">${this.icon('checkmark')}</span>
              </button>
            `).join('')}
          </div>
          <div class="hnq-question-actions">
            <button class="hnq-text-button" type="button" data-action="previous">${this.escape(this.currentIndex === 0 ? this.copy('backToIntro', 'Back to intro') : this.copy('back', 'Back'))}</button>
            <button class="button hnq-button" type="button" data-action="next" ${this.selected === null || this.selected === undefined ? 'disabled' : ''}>${this.escape(this.copy('next', 'Next question'))}</button>
          </div>
          ${didYouKnow ? `
            <aside class="hnq-insight hnq-insight--question">
              <span class="hnq-insight__icon" aria-hidden="true">${this.icon('info')}</span>
              <div><strong>Did you know?</strong><p>${this.escape(didYouKnow)}</p></div>
            </aside>
          ` : ''}
        </div>
      </div>
    `;
  }

  renderTransition() {
    const completedQuestion = this.currentIndex + 1;
    const transition = this.transitions.find((item) => Number(item.afterQuestion) === completedQuestion) || {};

    return `
      <div class="hnq hnq-card hnq-transition">
        ${this.renderQuestionProgress(completedQuestion, false)}
        <div class="hnq-transition__top">
          <span class="hnq-transition__brand" aria-hidden="true">${this.renderLoadingSpinner()}</span>
          <span class="hnq-step-count"><span>Checkpoint ·</span><strong>${completedQuestion}</strong><span>of ${this.total}</span></span>
        </div>
        <h2 data-hnq-screen-title tabindex="-1">${this.escape(transition.headline || 'Keep going')}</h2>
        <p>${this.escape(transition.body || transition.remaining || '')}</p>
        <div class="hnq-actions hnq-actions--center">
          <button class="button hnq-button" type="button" data-action="continue">${this.escape(transition.cta || 'Keep going')}</button>
        </div>
      </div>
    `;
  }

  renderCapture() {
    const capture = this.definition.capture || {};
    const captureHeadline = capture.headline && !/almost\s+there/i.test(String(capture.headline))
      ? capture.headline
      : 'Your result is ready';
    const captureSubtitle = this.isAnalysis
      ? 'Enter your name and email to discover your personal analysis, your answers explained, and a next step chosen for your result.'
      : capture.subtitle && !/^enter your name and email to reveal your result on this page\.?$/i.test(String(capture.subtitle).trim())
      ? capture.subtitle
      : 'Enter your name and email to reveal your score, analysis, corrected answers, and recommended Hearts Notes material.';
    const captureCta = capture.cta && !/see my (score|result)/i.test(String(capture.cta))
      ? capture.cta
      : 'Reveal my result';

    const preparingIntro = `
      <p class="hnq-eyebrow">Analyzing your replies</p>
      <h2 data-hnq-screen-title tabindex="-1">We’re preparing your personal result</h2>
      <p class="hnq-assessing__subtitle">${this.isAnalysis ? 'We are bringing together the patterns in your answers, your personal note, and the guidance connected to your result.' : 'We are reading your answers and organizing the score, explanations, and guidance connected to your result.'}</p>
    `;

    const readyContent = this.captureReady ? `
      <div class="hnq-capture__ready-content">
        ${this.renderReportHeading({
          icon: 'star',
          eyebrow: 'Your personal report',
          title: captureHeadline,
          body: captureSubtitle,
          modifier: 'capture'
        })}
        <div class="hnq-capture__form-area">
          <form class="hnq-form hnq-form--capture" data-hn-capture-form novalidate>
            <div class="hnq-field">
              <label for="hnq-first-name">First name</label>
              <input class="hnq-input" id="hnq-first-name" name="firstName" type="text" autocomplete="given-name" inputmode="text" value="${this.escape(this.draftFirstName)}" aria-invalid="${this.error && !this.draftFirstName ? 'true' : 'false'}" required>
            </div>
            <div class="hnq-field">
              <label for="hnq-email">Email address</label>
              <input class="hnq-input" id="hnq-email" name="email" type="email" autocomplete="email" inputmode="email" value="${this.escape(this.draftEmail)}" aria-invalid="${this.error && this.draftFirstName ? 'true' : 'false'}" aria-describedby="hnq-privacy" required>
            </div>
            <button class="button hnq-button" type="submit" ${this.isSubmitting ? 'disabled' : ''}>${this.escape(this.isSubmitting ? 'Preparing your result…' : captureCta)}</button>
          </form>
          <p class="hnq-privacy" id="hnq-privacy">${this.escape(this.isAnalysis ? 'Your analysis, every answer, and a personal letter will appear here after submission. We’ll never share your email.' : capture.privacyNote || 'Your score, every answer, and a personal letter will appear here after submission. We’ll never share your email.')}</p>
          ${this.error ? `<p class="hnq-error" role="alert">${this.escape(this.error)}</p>` : ''}
        </div>
        <div class="hnq-capture__actions">
          <button class="hnq-text-button hnq-capture__back" type="button" data-action="previous">← ${this.escape(this.copy('reviewLastAnswer', 'Review my last answer'))}</button>
          <button class="hnq-text-button hnq-capture__skip" type="button" data-action="skip-capture">${this.escape(this.copy('continueWithoutEmail', 'Continue without email'))}</button>
        </div>
      </div>
    ` : `
      <button class="hnq-text-button hnq-capture__back" type="button" data-action="previous">← ${this.escape(this.copy('reviewLastAnswer', 'Review my last answer'))}</button>
    `;

    return `
      <div class="hnq hnq-card hnq-capture ${this.captureReady ? 'hnq-capture--ready' : 'hnq-capture--preparing'}">
        ${this.captureReady ? '' : this.renderAssessmentProgress(false, preparingIntro)}
        ${readyContent}
      </div>
    `;
  }

  renderAssessmentProgress(isReport = false, intro = '') {
    const lines = isReport ? this.reportAssessmentLines : this.assessmentLines;
    const complete = isReport ? false : this.captureReady;

    return `
      <div class="hnq-assessing__progress ${complete ? 'is-complete' : ''}" aria-live="polite">
        <div class="hnq-assessing__event" aria-hidden="true">
          ${this.renderLoadingSpinner()}
        </div>
        ${intro}
        <div class="hnq-assessing__lines">
          ${lines.map((line, index) => {
            const visible = complete || index <= this.assessmentStep;
            const current = !complete && index === this.assessmentStep;
            const completed = complete || index < this.assessmentStep;
            return `
              <div class="hnq-assessing__line ${visible ? 'is-visible' : ''} ${current ? 'is-current' : ''} ${completed ? 'is-complete' : ''}" data-hnq-assessment-line aria-hidden="${!visible}">
                <span class="hnq-assessing__check" aria-hidden="true">${this.icon('checkmark')}</span>
                <span>${this.renderPersonalized(line)}</span>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  renderAssessing() {
    const headline = this.displayName
      ? `${this.displayName}, we’re building your full report`
      : 'We’re building your full report';
    const subline = 'We are checking every answer and preparing the explanations connected to your result.';

    const reportIntro = `
        <p class="hnq-eyebrow">Your full report</p>
        <h2 data-hnq-screen-title tabindex="-1">${this.renderPersonalized(headline)}</h2>
        <p class="hnq-assessing__subtitle">${this.escape(subline)}</p>
    `;

    return `
      <div class="hnq hnq-card hnq-assessing" aria-live="polite">
        ${this.renderAssessmentProgress(true, reportIntro)}
      </div>
    `;
  }

  renderScore() {
    const name = this.displayName;
    const scoreTitle = name ? `${name}, see what your answers reveal` : 'See what your answers reveal';
    const scoreImage = this.meta.image || this.definition.meta?.image || '';
    return `
      <div class="hnq hnq-card hnq-score">
        <div class="hnq-score__intro">
          <div class="hnq-transition__brand" aria-hidden="true">${this.renderLoadingSpinner()}</div>
          <p class="hnq-eyebrow">Your quiz result</p>
          <h2 data-hnq-screen-title tabindex="-1">${this.renderPersonalized(scoreTitle)}</h2>
          <p class="hnq-score__message">${this.isAnalysis ? 'Discover the patterns in your answers, what they may mean for you, and a personal note for your next step.' : 'Go beyond the number. Explore your answers, the explanations behind them, and a personal note for your next step.'}</p>
        </div>
        <div class="hnq-score-cta-panel">
          ${scoreImage ? `<div class="hnq-score-cta-panel__media" aria-hidden="true"><img src="${this.escape(scoreImage)}" alt=""></div>` : ''}
          <div class="hnq-score-cta-panel__content">
            <p class="hnq-score-cta-panel__label">Your full report</p>
            <p>${this.isAnalysis ? 'Your answers tell a story. Explore your personal analysis and the next step connected to it.' : 'Your score is only the beginning. Open the full picture, at your own pace.'}</p>
          </div>
          <button class="button hnq-button hnq-button--analysis" type="button" data-action="analysis">Open my full report</button>
        </div>
      </div>
    `;
  }

  getRecommendationCopy(firstName = this.displayName) {
    const settings = this.recommendationSettings;
    const matched = this.matchedRecommendation || {};
    const defaultHeadline = firstName
      ? `${firstName}, this is the material we recommend for your result`
      : 'This is the material we recommend for your result';

    return {
      eyebrow: matched.eyebrow || settings.eyebrow || settings.offerLabel || 'Recommended for you',
      headline: this.templateText(matched.headlineTemplate || matched.headline || settings.headlineTemplate || settings.materialHeadlineTemplate || settings.offerHeadline, defaultHeadline, firstName),
      body: this.templateText(matched.body || matched.reason || settings.body || settings.materialIntroText || settings.subtitle, 'We created these materials to help turn reflection into a simple next step: something you can read, keep, print, and return to when you need structure and encouragement.', firstName),
      primaryLinkLabel: matched.primaryLinkLabel || settings.primaryLinkLabel || settings.linkLabel || 'Discover this material',
      linkedLinkLabel: matched.linkedLinkLabel || settings.linkedLinkLabel || 'See more'
    };
  }

  renderAnalysisInsight() {
    const analysis = this.matchedAnalysis;
    if (!analysis || (!analysis.headline && !analysis.body)) return '';

    return `
      <div class="hnq-analysis-insight">
        <div class="hnq-analysis-insight__icon" aria-hidden="true">${this.icon('info')}</div>
        <div>
          ${analysis.headline ? `<h3>${this.renderPersonalized(this.templateText(analysis.headline))}</h3>` : ''}
          ${analysis.body ? `<p>${this.renderPersonalized(this.templateText(analysis.body))}</p>` : ''}
        </div>
      </div>
    `;
  }

  renderReportHeading({ icon, eyebrow, title, body = '', modifier = '', focus = false }) {
    return `
      <div class="hnq-report-heading ${modifier ? `hnq-report-heading--${modifier}` : ''}">
        <div class="hnq-reveal__icon ${modifier ? `hnq-reveal__icon--${modifier}` : ''}" aria-hidden="true">${icon === 'brand' || modifier === 'capture' ? `<span class="hnq-transition__brand">${this.renderLoadingSpinner()}</span>` : this.icon(icon)}</div>
        <p class="hnq-eyebrow">${this.escape(eyebrow)}</p>
        <h2 ${focus ? 'data-hnq-screen-title tabindex="-1"' : ''}>${this.renderPersonalized(title)}</h2>
        ${body ? `<p>${this.renderPersonalized(body)}</p>` : ''}
      </div>
    `;
  }

  renderReveal() {
    const letter = this.matchedLetter;
    const showProducts = this.dataset.showProducts === 'true' && this.productList().length > 0;
    const name = this.displayName;
    const greeting = name ? `${name}, here’s your full result` : 'Here’s your full result';

    return `
      <div class="hnq hnq-card hnq-reveal">
        ${this.renderReportHeading({
          icon: 'brand',
          eyebrow: 'Your full report',
          title: greeting,
          body: 'Take a moment to explore your result, review your answers, and read your personal note.',
          focus: true
        })}

        <section class="hnq-analysis-report-block">
          <div class="hnq-report-score">
            ${this.isAnalysis ? '<p class="hnq-eyebrow">What your answers suggest</p>' : `<p class="hnq-eyebrow">Your score</p><div class="hnq-score__number" aria-label="Score ${this.score} out of ${this.total}">${this.score}<span>/${this.total}</span></div>`}
            <h3>${this.escape(this.scoreBand.title || 'Your result')}</h3>
            ${this.scoreBand.message ? `<p>${this.renderPersonalized(this.templateText(this.scoreBand.message))}</p>` : ''}
          </div>
          <p class="hnq-eyebrow">Your Analysis Report</p>
          ${this.renderAnalysisInsight()}
          ${letter?.body ? this.renderPersonalNote(letter.body) : ''}
        </section>

        <section class="hnq-corrected-answers">
          ${this.renderReportHeading({
            icon: 'checkmark',
            eyebrow: this.isAnalysis ? 'Your answers, explained' : 'Corrected answers',
            title: 'Review your answers',
            modifier: 'answers'
          })}
          <div class="hnq-answer-list">
            ${this.questions.map((question, index) => this.renderAnswer(question, index)).join('')}
          </div>
        </section>

        ${showProducts ? this.renderProducts() : ''}
      </div>
    `;
  }


  renderPersonalNote(body) {
    const revealed = this.noteRevealed;
    return `
      <section class="hnq-note ${revealed ? 'is-revealed' : 'is-locked'} ${this.noteJustRevealed ? 'is-magic-reveal' : ''}">
        <div class="hnq-note__content">
          <p class="hnq-note__label">A private note for you</p>
          <div class="hnq-note__body"><p>${this.renderPersonalized(this.templateText(body))}</p></div>
        </div>
        ${revealed ? '' : `
          <div class="hnq-note__veil" aria-hidden="true"></div>
          <div class="hnq-note__cta-wrap">
            <button class="button hnq-note__button" type="button" data-action="toggle-note">
              <span class="hnq-note__button-main">View your private note</span>
              <span class="hnq-note__button-sub">Click here to reveal</span>
            </button>
          </div>
        `}
      </section>
    `;
  }


  renderAnswer(question, index) {
    const chosenIndex = this.answers[index];
    if (this.isAnalysis) return this.renderReflectionAnswer(question, index, chosenIndex);
    const isCorrect = chosenIndex === question.correct;
    const chosenText = question.options?.[chosenIndex] || this.copy('noAnswer', 'No answer selected');
    const correctText = question.options?.[question.correct] || '';

    return `
      <div class="hnq-answer ${isCorrect ? 'is-correct' : 'is-wrong'}">
        <div class="hnq-answer__head">
          <div class="hnq-answer__meta">
            <span class="hnq-answer__status">
              <span class="hnq-answer__status-icon" aria-hidden="true">${isCorrect ? this.icon('checkmark') : this.icon('warning')}</span>
              ${this.escape(isCorrect ? this.copy('correct', 'Correct') : this.copy('incorrect', 'Incorrect'))}
            </span>
            <span class="hnq-answer__index">${this.escape(this.copy('question', 'Question'))} ${index + 1}</span>
          </div>
          <div class="hnq-answer__prompt">
            <p class="hnq-answer__question-text">${this.escape(question.q)}</p>
          </div>
        </div>
        <div class="hnq-answer__details">
          <div class="hnq-answer__grid">
            <div class="hnq-answer__box ${isCorrect ? 'is-good' : 'is-wrong-answer'}">
              <span>${this.escape(this.copy('yourAnswer', 'Your answer'))}</span>
              <strong>${this.escape(chosenText)}</strong>
            </div>
            <div class="hnq-answer__box is-good">
              <span>${this.escape(this.copy('correctAnswer', 'Correct answer'))}</span>
              <strong>${this.escape(correctText)}</strong>
            </div>
          </div>
          ${question.context ? `<div class="hnq-answer__context"><strong><span aria-hidden="true">${this.icon('info')}</span> ${this.escape(this.copy('whyMatters', 'Why this matters'))}</strong><p>${this.escape(question.context)}</p></div>` : ''}
        </div>
      </div>
    `;
  }

  renderReflectionAnswer(question, index, chosenIndex) {
    const chosenText = question.options?.[chosenIndex] || this.copy('noAnswer', 'No answer selected');
    return `<div class="hnq-answer hnq-answer--reflection">
      <div class="hnq-answer__head"><span class="hnq-answer__index">Question ${index + 1}</span><p class="hnq-answer__question-text">${this.escape(question.q)}</p></div>
      <div class="hnq-answer__details"><div class="hnq-answer__box"><span>Your answer</span><strong>${this.escape(chosenText)}</strong></div>
      ${question.context ? `<div class="hnq-answer__context"><strong>Understanding this pattern</strong><p>${this.escape(question.context)}</p></div>` : ''}</div>
    </div>`;
  }

  renderProducts() {
    const products = this.productList();
    if (!products.length) return '';
    const copy = this.getRecommendationCopy();

    return `
      <section class="hnq-materials">
        ${this.renderReportHeading({
          icon: 'brand',
          eyebrow: copy.eyebrow,
          title: copy.headline,
          body: copy.body,
          modifier: 'materials'
        })}
        ${this.renderProductCard(products[0], true, copy)}
        ${products.length > 1 ? `<h3 class="hnq-materials__related-title">You may also like</h3><div class="hnq-product-grid hnq-product-grid--supplementary">${products.slice(1).map(product => this.renderProductCard(product, false, copy)).join('')}</div>` : ''}
      </section>
    `;
  }

  renderProductCard(product, primary = false, copy = {}) {
    const title = product.customShortNameText || product.shortTitleText || product.shortTitle || product.title || 'Hearts Notes product';
    const url = this.attributedUrl(product.url, 'quiz', primary ? 'primary_product' : `related_${product.handle || product.id}`);
    return `
      <a class="hnq-product-card ${primary ? 'hnq-product-card--primary' : 'hnq-product-card--supplementary'}" href="${this.escape(url)}">
        <div class="hnq-product-card__media">
          ${product.image ? `<img src="${this.escape(product.image)}" alt="${this.escape(title)}" loading="lazy">` : '<div class="hnq-product-card__placeholder"></div>'}
        </div>
        <div class="hnq-product-card__body">
          <h3 class="hnq-product-card__title">${this.escape(title)}</h3>
          <span class="hnq-product-card__link">Explore the product →</span>
        </div>
      </a>
    `;
  }

}

if (!customElements.get('hn-quiz')) {
  customElements.define('hn-quiz', HeartsNotesQuiz);
}
